import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import ActivityPanel from '../components/ActivityPanel';

const STAGES = ['prospecting','qualified','proposal','negotiation','closed_won','closed_lost'];

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  // Attach contact
  const [showAttach, setShowAttach] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // New deal form
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm] = useState({ name:'', amount:'', stage:'prospecting', close_date:'', notes:'' });
  const [savingDeal, setSavingDeal] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/accounts/${id}`)
      .then(r => {
        setAccount(r.data.account);
        setForm(r.data.account);
        setContacts(r.data.contacts);
        setDeals(r.data.deals);
      })
      .catch(() => navigate('/accounts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!contactSearch.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/contacts?search=${encodeURIComponent(contactSearch)}&limit=10`)
        .then(r => {
          const attachedIds = contacts.map(c => c.id);
          setSearchResults((r.data.contacts || []).filter(c => !attachedIds.includes(c.id)));
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [contactSearch, contacts]);

  const handleAttach = async (contactId) => {
    try {
      await api.post(`/accounts/${id}/contacts`, { contact_id: contactId });
      setContactSearch('');
      setSearchResults([]);
      setShowAttach(false);
      load();
    } catch (err) {
      alert('Failed to attach contact.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/accounts/${id}`, {
        ...form, owner_id: form.owner_id || null,
      });
      setAccount(r.data.account);
      setEditing(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Delete this account? This cannot be undone.')) return;
    await api.delete(`/accounts/${id}`);
    navigate('/accounts');
  };

  const handleDetachContact = async (contactId) => {
    await api.delete(`/accounts/${id}/contacts/${contactId}`);
    setContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const handleCreateDeal = async () => {
    if (!dealForm.name.trim()) return alert('Opportunity name is required.');
    setSavingDeal(true);
    try {
      await api.post(`/accounts/${id}/deals`, dealForm);
      setShowDealForm(false);
      setDealForm({ name:'', amount:'', stage:'prospecting', close_date:'', notes:'' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create opportunity.');
    } finally {
      setSavingDeal(false);
    }
  };

  const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });

  const stageBadge = s => {
    const map = { prospecting:'badge-gray', qualified:'badge-blue', proposal:'badge-amber',
                  negotiation:'badge-red', closed_won:'badge-green', closed_lost:'badge-gray' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s.replace('_',' ')}</span>;
  };

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>;
  if (!account) return null;

  return (
    <>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button className="btn" onClick={() => navigate('/accounts')}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:600 }}>{account.name}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2, display:'flex', gap:8, alignItems:'center' }}>
            <span>{[account.city, account.state, account.country].filter(Boolean).join(', ')}</span>
            {account.owner_name
              ? <span className="badge badge-blue">{account.owner_name}</span>
              : <span className="badge badge-gray">Unassigned</span>
            }
          </div>
        </div>
        <button className="btn" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</button>
        <button className="btn" onClick={handleDeleteAccount} style={{ color:'var(--danger)' }}>Delete</button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>Edit account</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[
              { key:'name', label:'Account name *', placeholder:'Account name' },
              
              { key:'website', label:'Website', placeholder:'https://...' },
              { key:'address_line1', label:'Address line 1', placeholder:'Street address' },
              { key:'address_line2', label:'Address line 2', placeholder:'Suite, unit, etc.' },
              { key:'city', label:'City', placeholder:'City' },
              { key:'state', label:'State', placeholder:'State' },
              { key:'zip', label:'ZIP code', placeholder:'ZIP' },
            ].map(f => (
              <div key={f.key}>
                <label className="form-label">{f.label}</label>
                <input className="form-input" value={form[f.key]||''} placeholder={f.placeholder}
                  onChange={e => setForm({...form, [f.key]:e.target.value})} />
              </div>
            ))}
            <div>
  <label className="form-label">Industry</label>
  <select className="form-input" value={form.industry||''}
    onChange={e => setForm({...form, industry:e.target.value})}>
    <option value="">— Select industry —</option>
    <option>Aerospace & Defense</option>
    <option>Automotive</option>
    <option>Communications</option>
    <option>Consumer</option>
    <option>Gaming</option>
    <option>Industrial</option>
    <option>Lighting</option>
    <option>Medical</option>
    <option>Security</option>
    <option>Solar</option>
    <option>Space</option>
  </select>
</div>
            <div>
              <label className="form-label">Account owner</label>
              <select className="form-input" value={form.owner_id||''}
                onChange={e => setForm({...form, owner_id:e.target.value})}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Main layout — left + right activity panel */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16, alignItems:'start' }}>

        {/* LEFT SIDE */}
        <div>
          <div className="two-col" style={{ marginBottom:16 }}>
            {/* Account Info */}
            <div className="card">
              <div className="section-title">Account details</div>
              <table className="data-table">
                <tbody>
                  <tr><td style={{ color:'var(--text-secondary)', width:120 }}>Owner</td>
                    <td>{account.owner_name
                      ? <span className="badge badge-blue">{account.owner_name}</span>
                      : <span className="badge badge-gray">Unassigned</span>}
                    </td>
                  </tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Industry</td><td>{account.industry || '—'}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Website</td>
                    <td>{account.website
                      ? <a href={account.website} target="_blank" rel="noreferrer" style={{ color:'var(--accent-dark)' }}>{account.website}</a>
                      : '—'}
                    </td>
                  </tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Address</td>
                    <td style={{ fontSize:13 }}>
                      {[account.address_line1, account.address_line2, account.city, account.state, account.zip, account.country]
                        .filter(Boolean).join(', ') || '—'}
                    </td>
                  </tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Open deals</td><td><span className="badge badge-blue">{account.open_deals}</span></td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Pipeline</td><td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(account.pipeline_value)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Contacts */}
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div className="section-title" style={{ marginBottom:0 }}>Contacts ({contacts.length})</div>
                <button className="btn" style={{ fontSize:12, height:28 }}
                  onClick={() => { setShowAttach(!showAttach); setContactSearch(''); setSearchResults([]); }}>
                  {showAttach ? 'Cancel' : '+ Attach contact'}
                </button>
              </div>
              {showAttach && (
                <div style={{ marginBottom:12 }}>
                  <input className="form-input" value={contactSearch}
                    onChange={e => setContactSearch(e.target.value)}
                    placeholder="Search contacts by name or email…" autoFocus />
                  {searchResults.length > 0 && (
                    <div style={{ border:'0.5px solid var(--border)', borderRadius:'var(--radius)',
                                   marginTop:4, background:'var(--bg)', overflow:'hidden' }}>
                      {searchResults.map(c => (
                        <div key={c.id} onClick={() => handleAttach(c.id)}
                          style={{ padding:'8px 12px', fontSize:13, cursor:'pointer',
                                   borderBottom:'0.5px solid var(--border)',
                                   display:'flex', justifyContent:'space-between', alignItems:'center' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-secondary)'}
                          onMouseLeave={e => e.currentTarget.style.background='var(--bg)'}>
                          <span>{c.first_name} {c.last_name}</span>
                          <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>{c.email || c.title || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {contactSearch.trim() && searchResults.length === 0 && (
                    <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:6 }}>
                      No contacts found. <span style={{ color:'var(--accent-dark)', cursor:'pointer' }}
                        onClick={() => navigate('/contacts')}>Create a new contact →</span>
                    </div>
                  )}
                </div>
              )}
              {contacts.length === 0
                ? <p style={{ fontSize:13, color:'var(--text-tertiary)' }}>No contacts attached.</p>
                : <table className="data-table">
                    <thead><tr><th>Name</th><th>Title</th><th>Email</th><th></th></tr></thead>
                    <tbody>
                      {contacts.map(c => (
                        <tr key={c.id}>
                          <td style={{ fontWeight:500 }}>{c.first_name} {c.last_name}</td>
                          <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{c.title || '—'}</td>
                          <td style={{ color:'var(--accent-dark)', fontSize:12 }}>{c.email || '—'}</td>
                          <td>
                            <button onClick={() => handleDetachContact(c.id)}
                              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:12 }}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </div>

          {/* Opportunities */}
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div className="section-title" style={{ marginBottom:0 }}>Opportunities ({deals.length})</div>
              <button className="btn btn-primary" style={{ fontSize:12, height:28 }}
                onClick={() => setShowDealForm(!showDealForm)}>
                {showDealForm ? 'Cancel' : '+ New opportunity'}
              </button>
            </div>
            {showDealForm && (
              <div style={{ background:'var(--bg-secondary)', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label className="form-label">Opportunity name *</label>
                    <input className="form-input" value={dealForm.name}
                      onChange={e => setDealForm({...dealForm, name:e.target.value})}
                      placeholder="e.g. Q3 Connector Order" />
                  </div>
                  <div>
                    <label className="form-label">Amount ($)</label>
                    <input className="form-input" type="number" value={dealForm.amount}
                      onChange={e => setDealForm({...dealForm, amount:e.target.value})} placeholder="0" />
                  </div>
                  <div>
                    <label className="form-label">Stage</label>
                    <select className="form-input" value={dealForm.stage}
                      onChange={e => setDealForm({...dealForm, stage:e.target.value})}>
                      {STAGES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Close date</label>
                    <input className="form-input" type="date" value={dealForm.close_date}
                      onChange={e => setDealForm({...dealForm, close_date:e.target.value})} />
                  </div>
                  <div style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Notes</label>
                    <input className="form-input" value={dealForm.notes}
                      onChange={e => setDealForm({...dealForm, notes:e.target.value})} placeholder="Optional notes" />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary" onClick={handleCreateDeal} disabled={savingDeal}>
                    {savingDeal ? 'Saving…' : 'Create opportunity'}
                  </button>
                  <button className="btn" onClick={() => setShowDealForm(false)}>Cancel</button>
                </div>
              </div>
            )}
            {deals.length === 0
              ? <p style={{ fontSize:13, color:'var(--text-tertiary)' }}>No opportunities yet.</p>
              : <table className="data-table">
                  <thead><tr><th>Name</th><th>Stage</th><th>Amount</th><th>Close date</th><th>Contact</th></tr></thead>
                  <tbody>
                    {deals.map(d => (
                      <tr key={d.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/pipeline/${d.id}`)}>
                        <td style={{ fontWeight:500 }}>{d.name}</td>
                        <td>{stageBadge(d.stage)}</td>
                        <td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(d.amount)}</td>
                        <td style={{ color:'var(--text-secondary)', fontSize:12 }}>
                          {d.close_date ? new Date(d.close_date).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{d.contact_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            }
          </div>
        </div>

        {/* RIGHT SIDE — Activity Panel */}
        <div className="card" style={{ position:'sticky', top:20 }}>
          <div className="section-title" style={{ marginBottom:12 }}>Activity</div>
          <ActivityPanel companyId={id} />
        </div>

      </div>
    </>
  );
}